import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Instagram, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, refreshUser, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [switching, setSwitching] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/instagram/auth-url");
      window.location.href = data.url;
    } catch { toast.error("OAuth error"); setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram?\n\nAuto-replies will stop firing for this account. You can reconnect anytime.")) return;
    setBusy(true);
    try {
      await api.post("/instagram/disconnect");
      toast.success("Disconnected");
      refreshUser();
    } catch { toast.error("Disconnect failed"); }
    finally { setBusy(false); }
  };

  const switchAccount = async () => {
    const handle = user?.ig_username ? `@${user.ig_username}` : "the current account";
    if (!window.confirm(
      `Switch Instagram account?\n\nThis will disconnect ${handle} and send you to Meta's consent screen so you can choose a different Instagram business account.\n\nYour keyword flows and message history are kept.`
    )) return;
    setSwitching(true);
    try {
      await api.post("/instagram/disconnect");
      const { data } = await api.get("/instagram/auth-url");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch account");
      setSwitching(false);
      refreshUser();
    }
  };

  return (
    <div className="p-5 sm:p-8 md:p-12 max-w-3xl">
      <div className="mb-8 sm:mb-12">
        <div className="overline mb-3">— Settings</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-tight" data-testid="settings-title">Account.</h1>
      </div>

      <div className="surface-card p-6 sm:p-8 mb-6" data-testid="settings-account">
        <h2 className="font-heading font-bold text-lg sm:text-xl mb-4">Profile</h2>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between gap-3"><span className="text-white/60">Name</span><span className="font-semibold truncate">{user?.full_name || "—"}</span></div>
          <div className="flex justify-between gap-3"><span className="text-white/60">Email</span><span className="font-mono truncate">{user?.email}</span></div>
          <div className="flex justify-between gap-3"><span className="text-white/60">Plan</span><span className="font-semibold capitalize">{user?.subscription_tier || "free"}</span></div>
        </div>
      </div>

      <div className="surface-card p-6 sm:p-8 mb-6" data-testid="settings-instagram">
        <h2 className="font-heading font-bold text-lg sm:text-xl mb-2 flex items-center gap-2"><Instagram className="w-5 h-5" /> Instagram</h2>
        {user?.ig_connected ? (
          <>
            <div className="text-sm text-white/70 mb-4">
              Connected as <span className="font-mono font-semibold text-klein">@{user.ig_username || user.ig_user_id}</span>
              {user.ig_business_id && (
                <div className="text-xs text-white/40 font-mono mt-1">Business account ID: {user.ig_business_id}</div>
              )}
            </div>
            <p className="text-xs text-white/50 mb-4 leading-relaxed">
              Have multiple Instagram business accounts? Use <strong className="text-white/70">Switch account</strong> below to disconnect this one and choose a different account on Meta's consent screen. Your keyword flows and message history are preserved.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={switchAccount} disabled={switching || busy} className="btn-primary w-full sm:w-auto justify-center" data-testid="settings-switch-ig">
                <RefreshCw className={`w-4 h-4 ${switching ? "animate-spin" : ""}`} /> {switching ? "Switching…" : "Switch account"}
              </button>
              <button onClick={disconnect} disabled={busy || switching} className="btn-secondary w-full sm:w-auto justify-center" data-testid="settings-disconnect-ig">
                Disconnect
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-white/70 mb-4">Connect your Instagram Business account to start automating DMs.</div>
            <button onClick={connect} disabled={busy} className="btn-primary w-full sm:w-auto justify-center" data-testid="settings-connect-ig">
              <Instagram className="w-4 h-4" /> {busy ? "Redirecting…" : "Connect Instagram"}
            </button>
          </>
        )}
      </div>

      <div className="surface-card p-6 sm:p-8" data-testid="settings-danger">
        <h2 className="font-heading font-bold text-lg sm:text-xl mb-4">Session</h2>
        <button onClick={logout} className="btn-secondary w-full sm:w-auto justify-center" data-testid="settings-logout"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    </div>
  );
}
